"use client"

import { useEffect, useRef, useState, type ReactNode } from "react"
import { cn } from "@/lib/utils"

/** Portrait iPhone screen recordings. */
export const PRODUCT_PHONE_ASPECT = "2574 / 3218"

export const ZOOM_MS = 1300

/** Soft landing — no abrupt stop at the end of the motion. */
const SCROLL_EASE = "cubic-bezier(0.33, 1, 0.68, 1)"

type ProductDemoCardProps = {
  delay?: number
  playDelay?: number
  playbackRate?: number
  src?: string
  poster?: string
  alt: string
  imageSrc?: string
}

/** Full-width portrait card — same footprint as the video on every breakpoint. */
export function ProductDemoCard({ delay = 0, playDelay = 0, playbackRate = 1, src, poster, alt, imageSrc }: ProductDemoCardProps) {
  const ref = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [shown, setShown] = useState(false)
  // The clip is fetched once the card is within a screen or so of the
  // viewport — early enough to be ready when it scrolls in, late enough that
  // page load doesn't pull every loop before the hero has painted.
  const [near, setNear] = useState(false)
  const [motionDone, setMotionDone] = useState(false)
  const [readyToPlay, setReadyToPlay] = useState(playDelay <= 0)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === "undefined") {
      setShown(true)
      setNear(true)
      setMotionDone(true)
      return
    }

    const nearIo = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setNear(true)
          nearIo.disconnect()
        }
      },
      { rootMargin: "900px 0px 900px 0px" },
    )
    nearIo.observe(el)

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true)
          io.disconnect()
        }
      },
      { rootMargin: "0px 0px -6% 0px", threshold: 0.12 },
    )

    io.observe(el)
    return () => {
      io.disconnect()
      nearIo.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!shown) return
    const timer = window.setTimeout(() => setMotionDone(true), ZOOM_MS + delay)
    return () => window.clearTimeout(timer)
  }, [shown, delay])

  useEffect(() => {
    if (playDelay <= 0) return
    const timer = window.setTimeout(() => setReadyToPlay(true), playDelay)
    return () => window.clearTimeout(timer)
  }, [playDelay])

  useEffect(() => {
    if (!readyToPlay || !src || !near) return

    const container = ref.current
    const video = videoRef.current
    if (!container || !video) return

    video.playbackRate = playbackRate

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.playbackRate = playbackRate
          void video.play().catch(() => {})
        } else {
          video.pause()
        }
      },
      { threshold: 0.08, rootMargin: "40px 0px 40px 0px" },
    )

    observer.observe(container)
    return () => observer.disconnect()
  }, [readyToPlay, src, near, playbackRate])

  return (
    <div ref={ref} className="group w-full">
      {/* Scroll zoom — outer shell */}
      <div
        className={cn(
          "relative w-full overflow-hidden rounded-[26px] bg-white ring-1 ring-black/[0.05]",
          "max-sm:shadow-none sm:shadow-[0_6px_24px_-18px_rgba(0,0,0,0.3)]",
          "dark:bg-white dark:ring-black/10",
          !motionDone && "sm:will-change-[opacity]",
          shown ? "opacity-100" : "opacity-90",
          "max-sm:transition-opacity max-sm:duration-700",
          "sm:transition-[opacity,box-shadow] sm:duration-[1300ms]",
          "motion-safe:sm:group-hover:shadow-[0_16px_44px_-18px_rgba(0,0,0,0.42)]",
          "motion-safe:sm:group-hover:duration-[900ms]",
        )}
        style={{
          aspectRatio: PRODUCT_PHONE_ASPECT,
          transitionTimingFunction: SCROLL_EASE,
          transitionDelay: motionDone ? "0ms" : `${delay}ms`,
        }}
      >
        {/* Hover zoom — inner layer, ease-in-out both ways */}
        {/* Both zooms live HERE, inside the shell's overflow-hidden, so the
            card's footprint never changes.

            The entrance zoom used to scale the SHELL: 1.06 on a 328px card is
            348px in a 328px cell, which is exactly the 20px grid gap. For the
            1300ms of the animation the three cards touched, then separated —
            the page looked like it was settling into place after load. Now the
            frame holds still and the screenshot inside it settles. */}
        <div
          className={cn(
            "relative h-full w-full origin-center",
            "motion-safe:max-sm:scale-100",
            "motion-safe:sm:transition-transform motion-safe:sm:ease-in-out",
            !shown && "product-zoom-in",
            motionDone
              ? "motion-safe:sm:duration-[900ms] motion-safe:sm:group-hover:scale-[1.045]"
              : "motion-safe:sm:duration-[1300ms] sm:will-change-transform",
          )}
          style={
            motionDone
              ? undefined
              : { transitionDelay: `${delay}ms`, transitionTimingFunction: SCROLL_EASE }
          }
        >
          {src ? (
            /* src is attached only once the card is near the viewport; until
               then the poster is the whole card. preload="auto" on three
               always-attached sources made the phone download every loop on
               page load, before the visitor had seen the hero. */
            <video
              ref={videoRef}
              muted
              loop
              playsInline
              disablePictureInPicture
              preload={near ? "auto" : "none"}
              poster={poster}
              src={near ? src : undefined}
              aria-label={alt}
              className="absolute inset-0 h-full w-full bg-white object-cover"
            />
          ) : imageSrc ? (
            /* eslint-disable-next-line @next/next/no-img-element */
            <img src={imageSrc} alt={alt} loading="lazy" className="absolute inset-0 h-full w-full bg-white object-cover" />
          ) : null}
        </div>
      </div>
    </div>
  )
}

/** @deprecated Use ProductDemoCard */
export function ProductDemoScrollReveal({ children }: { children: ReactNode; delay?: number }) {
  return <>{children}</>
}

export const PRODUCT_DEMO_CARD = ""
